import React, { useState, useRef, useEffect, useCallback } from 'react';
import Avatar from '../avatar/Avatar';
import ChatBoxAvatar from '../avatar/chatboxAvatar/ChatBoxAvatar';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { CHAT_GROUP_TYPE } from '../../global/constants';
import {
  Dropdown,
  Input,
  Option,
  OptionAvatar,
  OptionName,
  SelectContainer,
  SelectInput,
  Tag,
  TagAvatar,
  TagClose,
  TagName
} from './style';

interface UserInterface {
  id: string;
  name: string;
  email: string;
  phone: string;
  profile_photo?: string;
}

interface User {
  id: string;
  name: string;
  profile_photo?: string;
}

interface Group {
  id: string;
  name: string;
  type?: typeof CHAT_GROUP_TYPE.PROJECT | typeof CHAT_GROUP_TYPE.PERSONAL;
  users?: UserInterface[];
  profilePic?: string;
}

interface CustomSelectProps {
  users: User[];
  groups: Group[];
  currentUserId: string;
  value: string[];
  onChange: (value: string[], ref?: any) => void;
  onBlur: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  users,
  groups,
  currentUserId,
  value,
  onChange,
  onBlur,
  placeholder = 'Enter name or Project name',
  autoFocus = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onBlur();
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  const handleSelect = useCallback(
    (id: string) => {
      const newValue = [...value];
      const index = newValue.indexOf(id);
      if (index === -1) {
        newValue.push(id);
      } else {
        newValue.splice(index, 1);
      }
      setSearchTerm('');
      onChange(newValue);
    },
    [onChange, value]
  );

  const handleRemove = (id: string) => {
    const newValue = value.filter((item) => item !== id);
    onChange(newValue);
    setSearchTerm('');
  };

  const filteredUsers = users.filter(
    (user) => user.id !== currentUserId && user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredGroups = groups.filter(
    (group) => group.users && group.users.length > 2 && group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderTag = (id: string) => {
    const user = users.find((u) => u.id === id);
    const group = groups.find((g) => g.id === id);

    if (user) {
      return (
        <Tag key={id}>
          <TagAvatar>
            <Avatar imgSrc={user.profile_photo || ''} name={user.name || 'U N'} size={20} isNotBorder={true} />
          </TagAvatar>
          <TagName>{user.name}</TagName>
          <TagClose onClick={() => handleRemove(id)}>
            <SVGIcon name='message-close-icon' width='14' height='14' viewBox='0 0 14 14' className='fillColor' />
          </TagClose>
        </Tag>
      );
    }

    if (group) {
      return (
        <Tag key={id}>
          <TagAvatar>
            {group.type === CHAT_GROUP_TYPE.PROJECT ? (
              <SVGIcon
                name='teamcamp-mobile-icon'
                width='20'
                height='20'
                viewBox='0 0 30 30'
                className='teamcampicon'
              />
            ) : (
              <ChatBoxAvatar imgSrc={group.profilePic || ''} name={group.name || ''} size={20} users={group.users} />
            )}
          </TagAvatar>
          <TagName>{group.name}</TagName>
          <TagClose onClick={() => handleRemove(id)}>
            <SVGIcon name='message-close-icon' width='14' height='14' viewBox='0 0 14 14' className='fillColor' />
          </TagClose>
        </Tag>
      );
    }

    return null;
  };

  // Scroll selected option into view with improved behavior
  useEffect(() => {
    if (isOpen && optionRefs.current[currentIndex] && dropdownRef.current) {
      const selectedElement = optionRefs.current[currentIndex];
      const dropdownElement = dropdownRef.current;

      if (selectedElement) {
        const dropdownRect = dropdownElement.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();

        // Check if the selected element is outside the visible area
        if (selectedRect.top < dropdownRect.top) {
          // Element is above the visible area, scroll up
          dropdownElement.scrollTop -= dropdownRect.top - selectedRect.top;
        } else if (selectedRect.bottom > dropdownRect.bottom) {
          // Element is below the visible area, scroll down
          dropdownElement.scrollTop += selectedRect.bottom - dropdownRect.bottom;
        }
      }
    }
  }, [currentIndex, isOpen]);

  // Reset currentIndex when dropdown closes or search term changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [isOpen, searchTerm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allOptions = [...filteredUsers, ...filteredGroups];

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => (prevIndex === 0 ? allOptions.length - 1 : prevIndex - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setIsOpen(true);
          setCurrentIndex((prevIndex) => (prevIndex === allOptions.length - 1 ? 0 : prevIndex + 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (allOptions[currentIndex]) {
            const selectedItem = allOptions[currentIndex];
            handleSelect(selectedItem.id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          onBlur();
          break;
        default:
          break;
      }
    },
    [currentIndex, filteredUsers, filteredGroups, handleSelect, onBlur]
  );

  const handleMouseEnter = (index: number) => {
    setCurrentIndex(index);
  };

  const handleMouseLeave = () => {
    setCurrentIndex(-1); // or any other logic to reset focus
  };

  const isSelected = (id: string) => value.includes(id);

  return (
    <SelectContainer ref={containerRef}>
      <SelectInput onClick={() => setIsOpen(true)}>
        {value.map((id) => (
          <React.Fragment key={id}>{renderTag(id)}</React.Fragment>
        ))}
        <Input
          ref={inputRef}
          type='text'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          autoFocus={autoFocus}
        />
      </SelectInput>

      <Dropdown ref={dropdownRef} isOpen={isOpen} onKeyDown={handleKeyDown} tabIndex={-1}>
        {filteredUsers.map((user, index) => (
          <Option
            key={user.id}
            selected={isSelected(user.id)}
            focused={index === currentIndex}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleSelect(user.id)}
            style={{
              backgroundColor:
                isSelected(user.id) || index === currentIndex ? 'var(--neutral-secondary)' : 'transparent'
            }}>
            <OptionAvatar>
              <Avatar imgSrc={user.profile_photo || ''} name={user.name || 'U N'} size={28} isNotBorder={true} />
            </OptionAvatar>
            <OptionName>{user.name}</OptionName>
          </Option>
        ))}
        {filteredGroups.map((group, index) => (
          <Option
            key={`GROUP_${group.id}`}
            ref={(el) => (optionRefs.current[index + filteredUsers.length] = el)}
            selected={isSelected(group.id)}
            focused={index + filteredUsers.length === currentIndex}
            onMouseEnter={() => handleMouseEnter(index + filteredUsers.length)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleSelect(group.id)}
            style={{
              backgroundColor:
                isSelected(group.id) || index + filteredUsers.length === currentIndex
                  ? 'var(--neutral-secondary)'
                  : 'transparent'
            }}>
            <OptionAvatar>
              {group.type === CHAT_GROUP_TYPE.PROJECT ? (
                <SVGIcon
                  name='teamcamp-mobile-icon'
                  width='28'
                  height='28'
                  viewBox='0 0 30 30'
                  className='teamcampicon'
                />
              ) : (
                <ChatBoxAvatar imgSrc={group.profilePic || ''} name={group.name || ''} size={28} users={group.users} />
              )}
            </OptionAvatar>
            <OptionName>{group.name}</OptionName>
          </Option>
        ))}
      </Dropdown>
    </SelectContainer>
  );
};

export default CustomSelect;
